import * as B from './balance.js';
import { isAlly } from './diplomacy.js';
import { linkOptions } from './network.js';
import { commandTierFor, onboardingNext, TIER_MARKET, TIER_PRODUCE } from './onboarding.js';
import { defaultOrbit, freeSlotsOnOrbit, hasStructure } from './structures.js';
import { combatSize, scoutOnly } from './state.js';
import { colonyNetwork, colonyStockTotal, hasDecree, ownedSystems, rangeContext, reachableRegions, visibleSectors } from './world.js';
import { hexKey, hexNeighbors } from './hex.js';
/** Cards done by going somewhere: once taken (the player went there), they are not proposed again. */
export const LOOK_KINDS = new Set(['touch_star', 'enter_system', 'read_recap']);
const affordable = (stock, cost) => B.RESOURCE_LIST.every((r) => (cost[r] ?? 0) <= stock[r]);
/** Cheapest relay the colony could build right now, from any connected system. A star already ours is not a target:
 *  it is the far end of a relay still being built (it joins the Network when that relay is ready), issue #47. */
function cheapestLink(w, c, from = colonyNetwork(w, c).keys(), rctx = rangeContext(w, c)) {
    const net = colonyNetwork(w, c);
    let best = null;
    for (const a of from) {
        for (const o of linkOptions(w.galaxy, w.galaxy.systems[a], rctx)) {
            const st = w.systems[o.to.id];
            if (net.has(o.to.id) || st.owner === c.id || (st.owner && !isAlly(w, c.id, st.owner)))
                continue;
            if (w.relays[[a, o.to.id].sort().join('|')])
                continue;
            if (!best || o.verdict.cost.metal < best.cost.metal)
                best = { from: a, to: o.to.id, cost: o.verdict.cost };
        }
    }
    return best;
}
/** The first resource short of `cost` in the best of these warehouses, and by how much (null when one can pay). */
function shortfall(stocks, cost) {
    let best = null;
    for (const st of stocks) {
        const miss = B.RESOURCE_LIST.map((r) => ({ res: r, qty: Math.ceil((cost[r] ?? 0) - st[r]) })).filter((x) => x.qty > 0);
        if (!miss.length)
            return null;
        const total = miss.reduce((n, x) => n + x.qty, 0);
        if (!best || total < best.total)
            best = { res: miss[0].res, qty: miss[0].qty, total };
    }
    return best && { res: best.res, qty: best.qty };
}
export function counsel(w, c, max = 3) {
    const out = [];
    const tier = c.onboarding.tier;
    const stock = colonyStockTotal(w, c.id);
    const capital = w.galaxy.systems[c.capital];
    const capStock = w.systems[c.capital].stock;
    const owned = ownedSystems(w, c.id);
    const net = colonyNetwork(w, c);
    const name = (id) => w.galaxy.systems[id]?.name ?? id;
    // A card whose goal is a place to look (the star, the plateau, the recap) has no command to run: it is done once the
    // player has been there, which the client says with `counsel_answer` (taken). It then leaves the Counsel for good.
    const seenIds = new Set(c.journal.filter((j) => j.kind === 'counsel.taken' && j.note).map((j) => j.note));
    const push = (o) => {
        if (!o.command && LOOK_KINDS.has(o.kind) && seenIds.has(o.id))
            return;
        if (!o.command || commandTierFor(w, o.command) <= tier)
            out.push(o);
    };
    // A hostile warfleet heading for one of our systems: a turret there, or the idle fleet.
    const inbound = Object.values(w.fleets).find((f) => f.destination && owned.includes(f.destination) && f.owner !== c.id && !isAlly(w, c.id, f.owner) && combatSize(f.units) > 0);
    if (inbound?.destination) {
        const target = inbound.destination, sys = w.galaxy.systems[target];
        if (!w.systems[target].structures.some((s) => s.kind in B.TURRET_STATS && s.hp > 0) && freeSlotsOnOrbit(w, sys, defaultOrbit('turret_light')) > 0) {
            const cost = B.BUILDING_COST.turret_light;
            push({ id: `turret:${target}`, kind: 'turret', urgency: 2, cost, show: { kind: 'plateau', system: target, orbit: defaultOrbit('turret_light') },
                command: affordable(w.systems[target].stock, cost) ? { type: 'build', system: target, building: 'turret_light' } : null,
                params: { system: name(target), ships: combatSize(inbound.units), eta: Math.max(0, Math.round((inbound.arriveAt - w.time) / 60)) } });
        }
        const idle = Object.values(w.fleets).find((f) => f.owner === c.id && f.at && f.at !== target && combatSize(f.units) > 0 && f.order.kind !== 'convoy');
        if (idle?.at)
            push({ id: `defend:${target}`, kind: 'defend', urgency: 2, cost: {}, show: { kind: 'tab', tab: 'fleets' }, command: { type: 'fleet_order', fleet: idle.id, order: 'defend', target }, params: { system: name(target), ships: combatSize(idle.units) } });
    }
    // Energy for the relays: three Draws of upkeep in the warehouses, or the Network goes dark.
    const upkeep = Object.values(w.relays).filter((r) => r.owner === c.id).reduce((s, r) => s + r.upkeep, 0);
    if (upkeep > 0 && stock.energy < upkeep * 3 && c.credits > 5) {
        const regions = [...reachableRegions(w, c)];
        const region = regions.includes(capital.region) ? capital.region : regions[0];
        const price = Math.round(B.BASE_PRICE.energy * 1.2 * 100) / 100;
        const qty = Math.max(1, Math.min(Math.ceil(upkeep * 3 - stock.energy), Math.floor(c.credits / price)));
        push({ id: 'buy_energy', kind: 'buy_energy', urgency: 2, cost: { credits: Math.round(qty * price) }, show: { kind: 'tab', tab: 'market' },
            command: region ? { type: 'market_order', region, resource: 'energy', side: 'buy', qty, price } : null,
            params: { qty, credits: Math.round(qty * price), draws: Math.max(0, Math.floor(stock.energy / upkeep)) } });
    }
    // The first minute: look at your star, then the first relay, then the next ones as long as the Energy carries their
    // upkeep (the inverse of `buy_energy`; the old cap at six systems left the Counsel empty, issue #47).
    const link = cheapestLink(w, c);
    const hasRelay = Object.values(w.relays).some((r) => r.owner === c.id);
    const drawMin = Math.max(1, Math.ceil(((Math.floor(w.time / B.DRAW_INTERVAL_S) + 1) * B.DRAW_INTERVAL_S - w.time) / 60));
    if (tier === 0 && !hasRelay)
        push({ id: 'touch', kind: 'touch_star', urgency: 2, cost: {}, show: { kind: 'star', system: c.capital }, command: null, params: { system: name(c.capital) } });
    if (link && (!hasRelay || upkeep === 0 || stock.energy >= upkeep * 3)) {
        // Past the first relay, « Do it » keeps a Warehouse's worth of Metal (playtest 28.09: four « Do it » in a row
        // emptied the newcomer's Metal without a word); the card then says what is missing and when the Draw brings it.
        const need = hasRelay ? { ...link.cost, metal: link.cost.metal + (B.BUILDING_COST.warehouse.metal ?? 0) } : link.cost;
        const payable = affordable(w.systems[link.from].stock, need) || affordable(capStock, need);
        const params = { from: name(link.from), to: name(link.to), metal: Math.ceil(link.cost.metal), energy: Math.ceil(link.cost.energy) };
        // Not payable yet: say what is missing and when the Draw brings more.
        const miss = payable ? null : shortfall([w.systems[link.from].stock, capStock], need);
        if (miss)
            Object.assign(params, { missingQty: miss.qty, missingRes: miss.res, draw: drawMin });
        push({ id: `link:${link.to}`, kind: hasRelay ? 'link_more' : 'link_first', urgency: hasRelay ? 1 : 2, cost: link.cost, show: { kind: 'link', from: link.from, to: link.to },
            command: payable ? { type: 'build_relay', a: link.from, b: link.to } : null, params });
    }
    // Nothing in reach from the Network: say why, and the remedy (issue #47).
    if (!link && hasRelay && tier >= TIER_PRODUCE) {
        const building = Object.values(w.relays).filter((r) => r.owner === c.id && r.readyAt > w.time).sort((a, b) => a.readyAt - b.readyAt)[0];
        const ext = building && (net.has(building.a) ? building.b : building.a);
        if (building && ext) {
            push({ id: `reach:${ext}`, kind: 'reach', urgency: 1, cost: {}, show: { kind: 'star', system: ext }, command: null,
                params: { system: name(ext), min: Math.max(1, Math.ceil((building.readyAt - w.time) / 60)), readyAt: building.readyAt } });
        }
        else {
            const amp = amplifierOpening(w, c, net);
            if (amp) {
                const cost = B.BUILDING_COST.amplifier;
                push({ id: `amplifier:${amp.system}`, kind: 'amplifier', urgency: 1, cost, show: { kind: 'plateau', system: amp.system, orbit: defaultOrbit('amplifier') },
                    command: affordable(w.systems[amp.system].stock, cost) ? { type: 'build', system: amp.system, building: 'amplifier' } : null,
                    params: { system: name(amp.system), to: name(amp.to) } });
            }
            else {
                const decree = tier >= TIER_MARKET && !hasDecree(w, c, 'range') ? cheapestLink(w, c, net.keys(), { ...rangeContext(w, c), rangeMult: B.DECREE_RANGE_MULT }) : null;
                if (decree) {
                    const credits = B.DECREE_COST_CREDITS.range;
                    push({ id: 'decree:range', kind: 'range_decree', urgency: 1, cost: { credits }, show: { kind: 'tab', tab: 'colony' },
                        command: c.credits >= credits ? { type: 'decree', kind: 'range' } : null, params: { to: name(decree.to), credits, hours: B.DECREE_HOURS.range } });
                }
                else
                    push({ id: 'reach:edge', kind: 'reach', urgency: 0, cost: {}, show: { kind: 'star', system: c.capital }, command: null, params: {} });
            }
        }
    }
    // The plateau opens with the first relay: go and see where things get built, before the first building
    // (a capital starts with an extractor and a shipyard).
    const capState = w.systems[c.capital];
    if (tier === 1 && capState.structures.filter((s) => s.kind !== 'relay').length <= 2 && !capState.buildQueue.some((j) => j.building !== 'relay'))
        push({ id: 'enter', kind: 'enter_system', urgency: 2, cost: {}, show: { kind: 'plateau', system: c.capital, orbit: null }, command: null, params: { system: name(c.capital) } });
    // Warehouses full at the last Draw: a warehouse at the capital.
    const lost = B.RESOURCE_LIST.reduce((s, r) => s + c.lastOverflow[r], 0);
    if (lost > 0 && freeSlotsOnOrbit(w, capital, defaultOrbit('warehouse')) > 0) {
        const cost = B.BUILDING_COST.warehouse;
        push({ id: 'warehouse', kind: 'warehouse', urgency: 1, cost, show: { kind: 'plateau', system: c.capital, orbit: defaultOrbit('warehouse') },
            command: affordable(capStock, cost) ? { type: 'build', system: c.capital, building: 'warehouse' } : null, params: { lost: Math.round(lost), system: name(c.capital) } });
    }
    // Money on the table: the resource that overflows the most, sold at the Market.
    const surplus = ['metal', 'food', 'crystal', 'rium'].map((r) => ({ r, q: capStock[r] })).filter((x) => x.q >= 400).sort((a, b) => b.q - a.q)[0];
    if (surplus && tier >= 2) {
        const regions = [...reachableRegions(w, c)];
        const region = regions.includes(capital.region) ? capital.region : regions[0];
        const qty = Math.floor((surplus.q - 250) / 10) * 10, price = Math.round(B.BASE_PRICE[surplus.r] * 0.9 * 100) / 100;
        if (region && qty > 0)
            push({ id: `sell:${surplus.r}`, kind: 'sell_surplus', urgency: 1, cost: { [surplus.r]: qty }, show: { kind: 'tab', tab: 'market' }, command: { type: 'market_order', region, resource: surplus.r, side: 'sell', qty, price }, params: { resource: surplus.r, qty, credits: Math.round(qty * price) } });
    }
    // One building at a time, the most useful first: eyes, then the Market's reach, then yield (issue #47).
    const builds = [];
    // Eyes: an antenna reveals the neighbouring sectors.
    if (!owned.some((id) => hasStructure(w.systems[id], 'antenna')) && freeSlotsOnOrbit(w, capital, defaultOrbit('antenna')) > 0) {
        const cost = B.BUILDING_COST.antenna;
        if (tier >= TIER_PRODUCE && !w.systems[c.capital].buildQueue.some((j) => j.building === 'antenna'))
            builds.push({ id: 'antenna', kind: 'antenna', urgency: 0, cost, show: { kind: 'plateau', system: c.capital, orbit: defaultOrbit('antenna') }, command: affordable(capStock, cost) ? { type: 'build', system: c.capital, building: 'antenna' } : null, params: { system: name(c.capital) } });
    }
    // A Trading Post once the Market is open: one more market region (prefer a system in a region not yet reached).
    if (tier >= TIER_MARKET && !owned.some((id) => hasStructure(w.systems[id], 'tradepost') || w.systems[id].buildQueue.some((j) => j.building === 'tradepost'))) {
        const regions = reachableRegions(w, c);
        const at = owned.filter((id) => net.has(id) && canBuild(w, id, 'tradepost'))
            .sort((a, b) => Number(!regions.has(w.galaxy.systems[b].region)) - Number(!regions.has(w.galaxy.systems[a].region)) || Number(b === c.capital) - Number(a === c.capital) || (a < b ? -1 : 1))[0];
        if (at) {
            const cost = B.BUILDING_COST.tradepost;
            builds.push({ id: `tradepost:${at}`, kind: 'tradepost', urgency: 0, cost, show: { kind: 'plateau', system: at, orbit: defaultOrbit('tradepost') },
                command: affordable(w.systems[at].stock, cost) ? { type: 'build', system: at, building: 'tradepost' } : null, params: { system: name(at) } });
        }
    }
    // An Extractor on the richest connected outpost: +50 % on every Draw of its band.
    if (tier >= TIER_PRODUCE) {
        const outpost = owned.filter((id) => id !== c.capital && net.has(id) && canBuild(w, id, 'extractor'))
            .map((id) => ({ id, pay: affordable(w.systems[id].stock, B.BUILDING_COST.extractor), yield: w.galaxy.systems[id].baseYield }))
            .sort((a, b) => Number(b.pay) - Number(a.pay) || b.yield - a.yield || (a.id < b.id ? -1 : 1))[0];
        if (outpost) {
            const sys = w.galaxy.systems[outpost.id];
            builds.push({ id: `extractor:${outpost.id}`, kind: 'extractor', urgency: 0, cost: B.BUILDING_COST.extractor, show: { kind: 'plateau', system: outpost.id, orbit: defaultOrbit('extractor') },
                command: outpost.pay ? { type: 'build', system: outpost.id, building: 'extractor' } : null, params: { system: name(outpost.id), resource: sys.resource, band: sys.band } });
        }
    }
    if (builds[0])
        push(builds[0]);
    // A first pair of corvettes once the Shipyard is open and nothing flies.
    const warships = Object.values(w.fleets).some((f) => f.owner === c.id && combatSize(f.units) > 0);
    if (!warships && hasStructure(w.systems[c.capital], 'shipyard')) {
        const cost = {};
        for (const r of B.RESOURCE_LIST) {
            const x = (B.UNIT_COST.corvette[r] ?? 0) * 2;
            if (x)
                cost[r] = x;
        }
        if (affordable(capStock, cost))
            push({ id: 'train', kind: 'train', urgency: 0, cost, show: { kind: 'plateau', system: c.capital, orbit: null }, command: { type: 'train', system: c.capital, unit: 'corvette', count: 2 }, params: { system: name(c.capital), count: 2 } });
    }
    // A scout (issue #48): the scout, or an idle warship, goes to a sector we cannot see; after the first-minute cards.
    if (tier >= TIER_PRODUCE) {
        const scout = scoutMove(w, c);
        // The dedicated scout is the hour's adventure (issue #48): it outranks the housekeeping cards.
        if (scout)
            push({ id: `scout:${scout.to}`, kind: 'scout', urgency: scoutOnly(w.fleets[scout.fleet].units) ? 1 : 0, cost: {}, show: { kind: 'star', system: scout.to }, command: { type: 'fleet_order', fleet: scout.fleet, order: 'move', target: scout.to }, params: { system: name(scout.to), from: name(scout.from) } });
    }
    // A neighbour in sight (human or NPC, the first contact first) and no word exchanged: a non-aggression pact.
    const seen = visibleSectors(w, c);
    const rival = c.contact?.rival;
    const neighbour = Object.values(w.colonies).sort((a, b) => Number(b.id === rival) - Number(a.id === rival)).find((o) => o.id !== c.id && seen.has(w.galaxy.systems[o.capital].sector)
        && !Object.values(w.treaties).some((t) => (t.a === c.id && t.b === o.id) || (t.a === o.id && t.b === c.id))
        && !w.proposals.some((p) => (p.from === c.id && p.to === o.id) || (p.from === o.id && p.to === c.id)));
    if (neighbour && c.influence >= 7)
        push({ id: `treaty:${neighbour.id}`, kind: 'treaty', urgency: 1, cost: {}, show: { kind: 'tab', tab: 'diplomacy' }, command: { type: 'treaty', with: neighbour.id, kind: 'nap' }, params: { colony: neighbour.name } });
    // Learning: a word to the General, and the recap of the last Draw.
    if (!(c.policy.notes ?? '').trim())
        push({ id: 'doctrine', kind: 'doctrine', urgency: 0, cost: {}, show: { kind: 'tab', tab: 'general' }, command: null, params: {} });
    if (w.drawIndex >= 0)
        push({ id: `recap:${w.drawIndex}`, kind: 'read_recap', urgency: 0, cost: {}, show: { kind: 'tab', tab: 'log' }, command: null, params: { draw: w.drawIndex + 1 } });
    // Never an empty Counsel (issue #47): the next tier and what it waits for, or the next Draw at the last tier.
    if (!out.length) {
        const next = onboardingNext(w, c);
        const params = nextStepParams(next, w.time);
        out.push({ id: next ? `next:${next.tier}` : 'next', kind: 'next_step', urgency: 0, cost: {}, show: { kind: 'tab', tab: 'general' }, command: null, params });
    }
    return out.sort((a, b) => b.urgency - a.urgency).slice(0, max);
}
/** The last card's numbers: the next tier and what it still waits for (the General panel says the same). */
export function nextStepParams(next, now) {
    const params = { draw: Math.max(1, Math.ceil(((Math.floor(now / B.DRAW_INTERVAL_S) + 1) * B.DRAW_INTERVAL_S - now) / 60)) };
    if (!next)
        return params;
    params.tier = next.tier;
    for (const n of next.needs) {
        if (n.kind === 'age')
            params.min = Math.max(1, Math.ceil((n.at - now) / 60));
        else if (n.kind === 'unshielded')
            params.shield = Math.max(1, Math.ceil((n.at - now) / 60));
        else if (n.kind === 'connected' || n.kind === 'beacon')
            params.systems = n.need - n.have;
        else if (n.kind !== 'draw')
            params[n.kind] = 1; // the Draw: every line says when it comes
    }
    return params;
}
/** Whether a building may be placed at this system now: a free slot on its orbit, not already there or queued. */
function canBuild(w, id, kind) {
    const st = w.systems[id];
    return !hasStructure(st, kind) && !st.buildQueue.some((j) => j.building === kind) && freeSlotsOnOrbit(w, w.galaxy.systems[id], defaultOrbit(kind)) > 0;
}
/** The connected system where an Amplifier opens the cheapest relay to a star out of reach today, or null. */
function amplifierOpening(w, c, net) {
    const base = rangeContext(w, c);
    let best = null;
    for (const id of [...net.keys()].sort()) {
        if (w.systems[id].owner !== c.id || !canBuild(w, id, 'amplifier'))
            continue;
        const link = cheapestLink(w, c, [id], { ...base, amplifiers: new Set([...base.amplifiers, id]) });
        if (link && (!best || link.cost.metal < best.metal))
            best = { system: id, to: link.to, metal: link.cost.metal };
    }
    return best && { system: best.system, to: best.to };
}
/** An idle warship docked in one of our systems, and the nearest free star in a sector we do not see yet. */
function scoutMove(w, c) {
    const fleet = Object.values(w.fleets).filter((f) => f.owner === c.id && f.at && f.order.kind === 'idle' && !f.destination && (scoutOnly(f.units) || (combatSize(f.units) > 0 && w.systems[f.at].owner === c.id)))
        .sort((a, b) => Number(scoutOnly(b.units)) - Number(scoutOnly(a.units)) || (a.id < b.id ? -1 : 1))[0];
    if (!fleet?.at)
        return null;
    const seen = visibleSectors(w, c), home = w.galaxy.systems[fleet.at];
    const edge = new Set();
    for (const key of seen)
        for (const n of hexNeighbors(w.galaxy.sectors[key].hex)) {
            const k = hexKey(n);
            if (w.galaxy.sectors[k] && !seen.has(k))
                edge.add(k);
        }
    let best = null;
    for (const sys of Object.values(w.galaxy.systems)) {
        if (!edge.has(sys.sector) || w.systems[sys.id].owner)
            continue;
        const d = Math.hypot(sys.x - home.x, sys.y - home.y);
        if (!best || d < best.d || (d === best.d && sys.id < best.id))
            best = { id: sys.id, d };
    }
    return best && { fleet: fleet.id, from: fleet.at, to: best.id };
}
//# sourceMappingURL=counsel.js.map